/*
 * Copyright (c) 2025 by PROS, Inc.  All Rights Reserved.
 * This software is the confidential and proprietary information of
 * PROS, Inc. ("Confidential Information").
 * You may not disclose such Confidential Information, and may only
 * use such Confidential Information in accordance with the terms of
 * the license agreement you entered into with PROS.
 */
import { HitlStatus, ClientResponseStatus, } from './model.js';
// temporary token needed by lang flow for auth
const FLOW_TOKEN_HEADER_NAME = 'x-pros-dev-flow-token';
/**
 * Represents the HTTP client.
 */
export class PwcAiChatHttpClient {
    /**
     * Constructor.
     */
    constructor(authenticationToken, enpoint, flowToken) {
        this._authenticationToken = authenticationToken;
        this._endpoint = enpoint;
        this._flowToken = flowToken;
    }
    /**
     * @override
     */
    async getQueryStatus(queryId) {
        // do server call.
        const response = await fetch(`${this._endpoint}/chat/${queryId}`, {
            method: 'GET',
            headers: await this._getHeaders(),
        });
        // guard.
        if (!response.ok) {
            return PwcAiChatHttpClient._agentRequestFailureResponse(response);
        }
        // get the data.
        const data = await response.json();
        // return the data.
        return PwcAiChatHttpClient._successResponse(data);
    }
    /**
     * @override
     */
    async sendQuery(request) {
        // create the body.
        const body = JSON.stringify({
            ...request,
            prompt: PwcAiChatHttpClient._sanitizePrompt(request.prompt),
        });
        // do server call.
        const response = await fetch(`${this._endpoint}/chat`, {
            method: 'POST',
            headers: await this._getHeaders(),
            body,
        });
        // guard.
        if (!response.ok) {
            return PwcAiChatHttpClient._agentRequestFailureResponse(response);
        }
        // get the data.
        const data = await response.json();
        // return the data.
        return PwcAiChatHttpClient._successResponse(data);
    }
    /**
     * @override
     */
    async getHitlQuestion(sessionId) {
        // do server call.
        const response = await fetch(`${this._endpoint}/chat/hitl/${sessionId}`, {
            method: 'GET',
            headers: await this._getHeaders(),
        });
        if (response.status === 204) {
            // 204 : no question.
            return PwcAiChatHttpClient._successResponse({
                status: HitlStatus.NO_QUESTION,
            });
        }
        if (response.status === 200) {
            // 200 : question for the user.
            const responseData = await response.json();
            // guard.
            if (!responseData.key || !responseData.question) {
                return PwcAiChatHttpClient._requestFailureResponse();
            }
            return PwcAiChatHttpClient._successResponse({
                status: HitlStatus.QUESTION,
                ...responseData,
            });
        }
        // for all other status, we return 'processing' since it should never fails.
        return PwcAiChatHttpClient._successResponse({
            status: HitlStatus.NO_QUESTION,
        });
    }
    /**
     * @override
     */
    async sendHitlAnswer(key, answer) {
        // create the body.
        const body = JSON.stringify({
            answer: PwcAiChatHttpClient._sanitizePrompt(answer),
        });
        // do server call.
        const response = await fetch(`${this._endpoint}/chat/hitl/${key}`, {
            method: 'POST',
            headers: await this._getHeaders(),
            body,
        });
        if (response.status !== 204) {
            return PwcAiChatHttpClient._requestFailureResponse();
        }
        return PwcAiChatHttpClient._successResponse(undefined);
    }
    /**
     * Send a feedback reaction (positive or negative).
     */
    async sendFeedbackReaction(request) {
        // create the body.
        const body = JSON.stringify(request);
        // do server call.
        const response = await fetch(`${this._endpoint}/feedback/reaction`, {
            method: 'POST',
            headers: await this._getHeaders(),
            body,
        });
        if (!response.ok) {
            return PwcAiChatHttpClient._requestFailureResponse();
        }
        return PwcAiChatHttpClient._successResponse(undefined);
    }
    /**
     * Send a feedback comment.
     */
    async sendFeedbackComment(request) {
        // create the body.
        const body = JSON.stringify(request);
        // do server call.
        const response = await fetch(`${this._endpoint}/feedback/comment`, {
            method: 'POST',
            headers: await this._getHeaders(),
            body,
        });
        if (!response.ok) {
            return PwcAiChatHttpClient._requestFailureResponse();
        }
        return PwcAiChatHttpClient._successResponse(undefined);
    }
    /**
     * Get the authentication token value.
     *
     * @returns The authentication token.
     */
    async _getAuthenticationToken() {
        // case of the provider.
        if (typeof this._authenticationToken === 'function') {
            return this._authenticationToken();
        }
        // here the token is a simple string.
        return Promise.resolve(this._authenticationToken);
    }
    /**
     * Generate common request headers for all api calls
     *
     * @returns request header
     */
    async _getHeaders() {
        // get auth token.
        const token = await this._getAuthenticationToken();
        const headers = new Headers({
            Accept: 'application/json, text/plain',
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
        });
        if (this._flowToken) {
            headers.set(FLOW_TOKEN_HEADER_NAME, this._flowToken);
        }
        return headers;
    }
    /**
     * Create a success response.
     */
    static _successResponse(data) {
        return {
            status: ClientResponseStatus.SUCCESS,
            data,
        };
    }
    /**
     * Create an agent request failure response.
     */
    static async _agentRequestFailureResponse(response) {
        // 400.
        if (response.status === 400) {
            // get the data.
            const data = await response.json();
            // create the result object.
            const result = {
                status: 'BAD_REQUEST',
                ...data,
            };
            // validate it.
            if (result.errorID && result.errorMessage) {
                return result;
            }
        }
        // 500.
        if (response.status === 500) {
            // get the data.
            const data = await response.json();
            // create the result object.
            const result = {
                status: 'INTERNAL_SERVER_ERROR',
                ...data,
            };
            // validate it.
            if (result.errorID && result.errorMessage && result.requestID) {
                return result;
            }
        }
        // uncategorized error.
        return Promise.resolve({
            status: 'UNCATEGORIZED_ERROR',
        });
    }
    /**
     * Create a request failure response.
     */
    static _requestFailureResponse() {
        return {
            status: ClientResponseStatus.UNCATEGORIZED_ERROR,
        };
    }
    /**
     * Sanitize a prompt.
     */
    static _sanitizePrompt(prompt) {
        return prompt.replace(/\s+/g, ' ');
    }
}
//# sourceMappingURL=pwc-ai-chat-http-client.js.map